import classNames from 'classnames';
import { Switch, Typography } from '@material-ui/core';
import PropTypes from 'prop-types';
import Paper from '@material-ui/core/Paper';
import AccountAccordionList from 'Components/UI/Accounts/AccountForm/AccountAccordionList';
import React, { useState } from 'react';
import { useAccountStateContext } from 'Components/Accounts/AccountContext';
import { withStyles } from 'Components/ComponentLibrary/ExternalLibraries/Libraries/MaterialV4/styles';
import { orderCollection } from 'Helpers/Utils';
import ArrowUpwardIcon from '@material-ui/icons/ArrowUpward';
import ArrowDownwardIcon from '@material-ui/icons/ArrowDownward';
import Button from 'Components/UI/Button';
import fr from 'facet-rest';
import { navigateToPageInNewTab } from 'Views/Page/Content/Templates';
import useSimpleHousehold from 'Hooks/useSimpleHousehold';

const SortableTH = ({
  onClick,
  isSelected,
  direction,
  className,
  children: childrenProp,
}) => {
  let Icon = null;
  if (isSelected) {
    Icon = direction === 'asc' ? (
      <ArrowUpwardIcon fontSize="small" color="primary" />
    ) : (
      <ArrowDownwardIcon fontSize="small" color="primary" />
    );
  }
  return (
    <div // eslint-disable-line jsx-a11y/click-events-have-key-events
      tabIndex="-1"
      role="button"
      style={{
        cursor: 'pointer',
      }}
      className={className}
      onClick={onClick}
    >
      {childrenProp}
      {Icon}
    </div>
  );
};

SortableTH.propTypes = {
  className: PropTypes.string.isRequired,
  onClick: PropTypes.func.isRequired,
  isSelected: PropTypes.bool.isRequired,
  direction: PropTypes.string.isRequired,
  children: PropTypes.any.isRequired,
};

const getNextDirection = dir => {
  if (!dir || dir === 'desc') {
    return 'asc';
  }

  return 'desc';
};

function AccountTab({ classes, ...rest }) {
  const { householdResource } = useSimpleHousehold();
  const { accountCollection } = useAccountStateContext();
  const [showAll, setShowAll] = React.useState(true);
  const [[selectedAttr, orderDirection], setOrderState] = useState([
    'balance',
    'desc',
  ]);

  const makeSortableOnClick = name => () => setOrderState([
    name,
    getNextDirection(name === selectedAttr ? orderDirection : false),
  ]);

  const showOpenAccountButton = householdResource.withRelationship(fr.relationships.plannerAccountApplication.name, () => { return true; }, () => { return false; });

  return (
    <div className={classNames(classes.root)} {...rest}>
      <div className={classes.filters}>
        <Typography
          component="span"
          variant="h3"
          color={showAll ? 'primary' : ''}
        >
          All
        </Typography>
        <Switch
          checked={!showAll}
          onClick={() => setShowAll(!showAll)}
          color="default"
        />
        <Typography
          component="span"
          variant="h3"
          color={!showAll ? 'primary' : ''}
        >
          AUM
        </Typography>
        {showOpenAccountButton && (
          <Button
            variant="contained"
            color="secondary"
            test="open-account"
            onClick={() => {
              navigateToPageInNewTab(householdResource.relationship(fr.relationships.plannerAccountApplication.name).link('related').url(), true);
            }}
          >
            Open Account
          </Button>
        )}
      </div>
      <Paper className={classes.ths} square>
        <SortableTH
          className={classes.th}
          direction={orderDirection}
          onClick={makeSortableOnClick('type')}
          isSelected={selectedAttr === 'type'}
        >
          <Typography variant="body1" color="primary" noWrap>
            Type
          </Typography>
        </SortableTH>
        <SortableTH
          className={classes.th}
          direction={orderDirection}
          onClick={makeSortableOnClick('institution')}
          isSelected={selectedAttr === 'institution'}
        >
          <Typography variant="body1" color="primary" noWrap>
            Institution
          </Typography>
        </SortableTH>
        <SortableTH
          className={classes.th}
          direction={orderDirection}
          onClick={makeSortableOnClick('name')}
          isSelected={selectedAttr === 'name'}
        >
          <Typography variant="body1" color="primary" noWrap>
            Name
          </Typography>
        </SortableTH>
        <SortableTH
          className={classes.th}
          direction={orderDirection}
          onClick={makeSortableOnClick('balance')}
          isSelected={selectedAttr === 'balance'}
        >
          <Typography variant="body1" color="primary" noWrap>
            Balance
          </Typography>
        </SortableTH>
        <Typography variant="body1" color="primary" noWrap>
          Status
        </Typography>
      </Paper>
      <AccountAccordionList
        accounts={orderCollection(
          accountCollection,
          selectedAttr,
          orderDirection,
        )}
        onlyManaged={!showAll}
      />
    </div>
  );
}

export default withStyles(theme => ({
  root: {
    gridGap: theme.spacing(1),
    width: '100%',
    height: '100%',
    placeItems: 'stretch',
    minWidth: 500,
  },
  tab: {
    margin: theme.spacing(0, 0.5),
  },
  ths: {
    display: 'flex',
    height: 32,
    backgroundColor: theme.palette.facet.lightGray,
    padding: theme.spacing(1),
  },
  table: {
    display: 'flex',
  },
  th: {
    flexGrow: 1,
    alignItems: 'center',
    width: `${(1 / 5) * 100}%`,
    maxWidth: `${(1 / 5) * 100}%`,
    marginLeft: 2,
    display: 'flex',
  },
  row: {
    height: 48,
  },
  selectedRow: {},
  filters: {
    display: 'flex',
    alignItems: 'center',
    paddingBottom: theme.spacing(2),
    gap: theme.spacing(2),
  },
  openAccountButton: {
    marginLeft: 'auto',
  },
}))(AccountTab);
